import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { ArrowsClockwise, PaperPlaneTilt, CheckCircle, Eye, EyeSlash, WarningCircle, Envelope, Clock } from "@phosphor-icons/react";

const KINDS = {
  invoice: "Facture", quote: "Soumission", contract: "Contrat", contract_reminder: "Rappel contrat", contract_non_renewal: "Avis non-renouvellement",
  receipt: "Reçu de paiement", client_invite: "Invitation client", supplier_invite: "Invitation fournisseur", supplier_access: "Accès fournisseur",
  password_reset: "Mot de passe", autre: "Autre",
};
const STATUS = {
  sent: { label: "Accepté par le serveur", cls: "bg-blue-50 text-blue-800 border-blue-200", Icon: PaperPlaneTilt },
  delivered: { label: "Livré (confirmé)", cls: "bg-emerald-50 text-emerald-800 border-emerald-200", Icon: CheckCircle },
  partial: { label: "Partiellement refusé", cls: "bg-amber-50 text-amber-800 border-amber-200", Icon: WarningCircle },
  bounced: { label: "Non livré (retour)", cls: "bg-red-50 text-red-700 border-red-200", Icon: WarningCircle },
  failed: { label: "Échec d'envoi", cls: "bg-red-50 text-red-700 border-red-200", Icon: WarningCircle },
};
const fmt = (iso) => iso ? new Date(iso).toLocaleString("fr-CA", { dateStyle: "short", timeStyle: "short" }) : "—";

export default function Emails() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  const [status, setStatus] = useState("all");
  const [syncing, setSyncing] = useState(false);

  const load = () => {
    const params = { limit: 300 };
    if (q) params.q = q;
    if (kind !== "all") params.kind = kind;
    if (status !== "all") params.status = status;
    api.get("/emails", { params }).then((r) => setRows(r.data)).catch(() => {});
    api.get("/emails/stats").then((r) => setStats(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [kind, status]); // eslint-disable-line react-hooks/exhaustive-deps

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/emails/sync");
      if (data.ok === false) toast.info(data.detail);
      else toast.success(`${data.notifications} notification(s) lue(s), ${data.updated} courriel(s) mis à jour`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSyncing(false); }
  };

  const tiles = stats ? [
    { label: "Envoyés", value: stats.total, Icon: Envelope, accent: "text-foreground" },
    { label: "Livraison confirmée", value: stats.by_status?.delivered || 0, Icon: CheckCircle, accent: "text-emerald-700" },
    { label: "Ouverts / lus", value: stats.opened, Icon: Eye, accent: "text-sky-700" },
    { label: "Non livrés / échecs", value: (stats.by_status?.bounced || 0) + (stats.by_status?.failed || 0), Icon: WarningCircle, accent: (stats.by_status?.bounced || 0) + (stats.by_status?.failed || 0) > 0 ? "text-red-600" : "text-foreground" },
  ] : [];

  return (
    <div className="space-y-6" data-testid="emails-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tighter">Courriels envoyés</h1>
          <p className="text-muted-foreground mt-1">Journal de tous les envois (factures, soumissions, contrats, invitations…) avec suivi de livraison et de lecture.</p>
        </div>
        <div className="flex items-center gap-3">
          {stats?.last_sync && <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock size={12} /> Dernière vérification : {fmt(stats.last_sync)}</span>}
          <Button data-testid="emails-sync-btn" variant="outline" className="rounded-sm" onClick={sync} disabled={syncing}><ArrowsClockwise size={16} className={`mr-2 ${syncing ? "animate-spin" : ""}`} /> Vérifier les accusés (IMAP)</Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {tiles.map((t) => (
          <div key={t.label} className="border border-border bg-card p-5 rounded-sm" data-testid={`emails-tile-${t.label}`}>
            <div className="flex items-center justify-between"><span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{t.label}</span><t.Icon size={18} className={t.accent} /></div>
            <div className={`mt-3 font-heading text-2xl font-bold tabular-nums ${t.accent}`}>{t.value}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <Input data-testid="emails-search" className="rounded-sm max-w-xs" placeholder="Rechercher (courriel, sujet, numéro, client…)" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} />
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger data-testid="emails-kind-filter" className="rounded-sm w-56"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tous les types</SelectItem>{Object.entries(KINDS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="emails-status-filter" className="rounded-sm w-56"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tous les statuts</SelectItem>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
        </Select>
        <Button variant="outline" size="sm" className="rounded-sm" onClick={load}>Filtrer</Button>
      </div>

      <div className="border border-border bg-card rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>Date</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Document / client</TableHead>
              <TableHead>Destinataires</TableHead>
              <TableHead>Sujet</TableHead>
              <TableHead>Livraison</TableHead>
              <TableHead>Lecture</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">Aucun courriel enregistré pour ces critères. Les envois sont journalisés à partir de maintenant.</TableCell></TableRow>}
            {rows.map((r) => {
              const st = STATUS[r.status] || STATUS.sent;
              const opened = !!r.opened_at;
              return (
                <TableRow key={r.id} data-testid={`email-row-${r.id}`}>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{fmt(r.sent_at)}</TableCell>
                  <TableCell className="text-sm">{KINDS[r.kind] || r.kind}</TableCell>
                  <TableCell className="text-sm"><span className="font-mono">{r.ref_number || ""}</span>{r.client_name && <span className="block text-xs text-muted-foreground">{r.client_name}</span>}</TableCell>
                  <TableCell className="text-sm">
                    {(r.to || []).map((e) => <span key={e} className={`block ${r.refused?.[e] ? "text-red-600 line-through" : ""}`}>{e}</span>)}
                    {(r.cc || []).map((e) => <span key={e} className="block text-xs text-muted-foreground">cc : {e}</span>)}
                  </TableCell>
                  <TableCell className="text-sm max-w-[260px] truncate" title={r.subject}>{r.subject}{r.attachment && <span className="block text-xs text-muted-foreground">📎 {r.attachment}</span>}</TableCell>
                  <TableCell>
                    <TooltipProvider><Tooltip><TooltipTrigger asChild>
                      <span data-testid={`email-status-${r.id}`} className={`inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-xs font-medium ${st.cls}`}><st.Icon size={12} /> {st.label}</span>
                    </TooltipTrigger><TooltipContent className="max-w-xs text-xs">
                      {r.status === "sent" && "Le serveur de courriel a accepté le message. La livraison finale est confirmée lorsqu'un accusé (DSN) revient dans la boîte IMAP."}
                      {r.status === "delivered" && `Accusé de livraison reçu le ${fmt(r.delivered_at)}.`}
                      {r.status === "bounced" && `Retour de courrier le ${fmt(r.bounced_at)} : ${r.bounce_subject || ""}`}
                      {r.status === "failed" && (r.error || "Échec SMTP")}
                      {r.status === "partial" && `Refusés : ${Object.keys(r.refused || {}).join(", ")}`}
                    </TooltipContent></Tooltip></TooltipProvider>
                  </TableCell>
                  <TableCell>
                    <span data-testid={`email-read-${r.id}`} className={`inline-flex items-center gap-1 text-xs ${opened ? "text-sky-700 font-medium" : "text-muted-foreground"}`}>
                      {opened ? <Eye size={14} /> : <EyeSlash size={14} />}
                      {r.read_receipt_at ? `Accusé de lecture ${fmt(r.read_receipt_at)}` : opened ? `Ouvert ${fmt(r.opened_at)}${r.open_count > 1 ? ` (×${r.open_count})` : ""}` : "Non ouvert"}
                    </span>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <p className="text-xs text-muted-foreground">La détection d'ouverture repose sur une image invisible dans le courriel : certains logiciels bloquent les images, un courriel « non ouvert » a donc pu être lu. Les accusés de livraison et de lecture dépendent du serveur et du logiciel du destinataire ; ils sont relevés automatiquement toutes les 30 minutes dans la boîte IMAP configurée.</p>
    </div>
  );
}
